import React from "react";
import { getCurrencySymbol, formatTaxPercent, formatAmount } from "../../../helper/formatCurrency";
import { useRestaurantConfig } from "../../../utils/restaurantConfig";

/**
 * Order Summary Component
 * Displays order totals and calculations breakdown
 *
 * @param {Object} props
 * @param {number} props.subtotal - Order subtotal before taxes and charges
 * @param {number} props.gstAmount - Tax amount calculated (VAT/GST from DB)
 * @param {number} props.gstPercent - Tax percentage rate
 * @param {number} props.grandTotal - Final total amount
 * @param {number} props.packageCharge - Total packaging charges
 */
const OrderSummary = ({
  subtotal,
  gstAmount,
  gstPercent,
  grandTotal,
  packageCharge,
}) => {
  const { locale, taxLabel } = useRestaurantConfig();
  const currencySymbol = getCurrencySymbol(locale);

  return (
    <div className="space-y-2">
      {/* Subtotal */}
      <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
        <span className="text-sm text-gray-600">Subtotal</span>
        <span className="text-sm font-medium text-gray-900">
          {currencySymbol}
          {formatAmount(subtotal).toFixed(2)}
        </span>
      </div>

      {/* Tax (VAT/GST from DB) */}
      <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
        <span className="text-sm text-gray-600">{taxLabel} ({formatTaxPercent(gstPercent)}%)</span>
        <span className="text-sm font-medium text-gray-900">
          {currencySymbol}
          {formatAmount(gstAmount).toFixed(2)}
        </span>
      </div>

      {/* Package Charge */}
      {packageCharge > 0 && (
        <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
          <span className="text-sm text-gray-600">Packaging Charge</span>
          <span className="text-sm font-medium text-gray-900">
            {currencySymbol}
            {formatAmount(packageCharge).toFixed(2)}
          </span>
        </div>
      )}

      {/* Grand Total */}
      <div className="flex justify-between items-center pt-2 mt-1.5 border-t-2 border-gray-200">
        <span className="text-base font-semibold text-gray-900">
          Grand Total
        </span>
        <span className="text-lg font-bold text-green-600">
          {currencySymbol}
          {formatAmount(grandTotal).toFixed(2)}
        </span>
      </div>
    </div>
  );
};

export default OrderSummary;
